import React, { useState } from 'react';
import AuthForm from './AuthForm';
import AuthField from './AuthField';
import AuthFormGroup from './AuthFormGroup';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Link from '@material-ui/core/Link';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import Markdown from '../../../components/Markdown';
import { useContent } from '../../../components/ContentProvider';
import * as Validate from '../../../components/Validate';

const ProviderSignupForm = ({title='Create your Clickability Provider Account', onComplete, loginLink, busy, signupError, buttonLabel='Sign Up', provider}) => {
  const content = useContent('/providers/signup') || {};
  const [email, setEmail] = useState('');
  const [emailWarning, setEmailWarning] = useState();
  const [username, setUsername] = useState('');
  const [usernameWarning, setUsernameWarning] = useState();
  const [firstName, setFirstName] = useState('');
  const [firstNameWarning, setFirstNameWarning] = useState();
  const [lastName, setLastName] = useState('');
  const [lastNameWarning, setLastNameWarning] = useState();
  const [contactPhone, setContactPhone] = useState('');
  const [contactPhoneWarning, setContactPhoneWarning] = useState();
  const [emailListAgreed, setEmailListAgreed] = useState(true);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTermsWarning, setShowTermsWarning] = useState(false);
  const [organisation, setOrganisation] = useState(provider ? provider.name : '');
  const [organisationWarning, setOrganisationWarning] = useState();
  const [abn, setAbn] = useState('');
  const [abnWarning, setAbnWarning] = useState();
  const [entityName, setEntityName] = useState('');
  const [entityNameWarning, setEntityNameWarning] = useState();

  const signup = async () => {
    const valid = [
      await Validate.validateProviderEmail(email, setEmailWarning),
      await Validate.validateProviderUsername(username, setUsernameWarning),
      Validate.validateFirstName(firstName, setFirstNameWarning),
      Validate.validateLastName(lastName, setLastNameWarning),
      Validate.validatePhoneNumber(contactPhone, setContactPhoneWarning),
      Validate.validateOrganisation(organisation, setOrganisationWarning),
      Validate.validateAbn(abn, setAbnWarning),
      Validate.validateEntityName(entityName, setEntityNameWarning),
      validateTermsAgreed(),
      true,
    ];
    if (valid.every(v=>v)) {
      onComplete({
        email: email,
        username: username,
        organisation: organisation,
        first_name: firstName,
        last_name: lastName,
        contact_phone: contactPhone,
        abn: abn,
        entity_name: entityName
      });
    }
  };

  const validateTermsAgreed = () => {
    if (!termsAgreed) {
      setShowTermsWarning(true);
      return false;
    }
    setShowTermsWarning(false);
    return true;
  };

  const emailListCheckboxHandler = () => {
    setEmailListAgreed(!emailListAgreed);
  };

  const termsCheckboxHandler = () => {
    const currentState = termsAgreed;
    setTermsAgreed(!currentState);
    if (!currentState) {
      setShowTermsWarning(false);
    }
  };


  return (
    <React.Fragment>
      <AuthForm
        buttonLabel={buttonLabel}
        buttonAction={signup}
        busy={busy}
      >
        <Typography variant="h1" color="primary">{title}</Typography>
        <Markdown content={content.body1}/>
        {signupError &&
          <Alert severity="error">
            {signupError}
          </Alert>
        }
        <AuthFormGroup title="Contact Details">

          <AuthField
            id="firstname"
            placeholder="Your first name"
            label="First Name*"
            autoComplete="given-name"
            type="text"
            name="firstname"
            value={firstName}
            onChange={setFirstName}
            error={firstNameWarning}
            onBlur={() => Validate.validateFirstName(firstName, setFirstNameWarning, true)}
          />

          <AuthField
            id="lastname"
            placeholder="Your last name"
            label="Last Name*"
            autoComplete="family-name"
            type="text"
            name="lastname"
            value={lastName}
            onChange={setLastName}
            error={lastNameWarning}
            onBlur={() => Validate.validateLastName(lastName, setLastNameWarning, true)}
          />

          <AuthField
            id="email"
            placeholder="Your email address"
            label="Email*"
            autoComplete="email"
            type="email"
            name="email"
            value={email}
            onChange={setEmail}
            error={emailWarning}
            onBlur={() => Validate.validateProviderEmail(email, setEmailWarning, true)}
          />

          <AuthField
            id="contactphone"
            placeholder="Contact Telephone Number"
            label="Contact Telephone*"
            autoComplete="tel-national"
            type="text"
            name="contactphone"
            value={contactPhone}
            onChange={setContactPhone}
            error={contactPhoneWarning}
            onBlur={() => Validate.validatePhoneNumber(contactPhone, setContactPhoneWarning, true)}
          />

          <AuthField
            id="username"
            placeholder="This is the name you will be known by on Clickability."
            label="Username*"
            autoComplete="username"
            type="text"
            name="username"
            value={username}
            onChange={setUsername}
            error={usernameWarning}
            onBlur={() => {
              const value = username.trim();
              setUsername(value);
              Validate.validateProviderUsername(value, setUsernameWarning, true);
            }}
          />

        </AuthFormGroup>

        <AuthFormGroup title="Company Details">

          <AuthField
            id="organisation"
            placeholder="Your organisation's trading name"
            label="Trading Name*"
            type="text"
            name="organisation"
            value={organisation}
            onChange={setOrganisation}
            disabled={provider !== undefined}
            error={organisationWarning}
            onBlur={() => Validate.validateOrganisation(organisation, setOrganisationWarning, true)}
          />

          <AuthField
            id="abn"
            placeholder="Your organisation's ABN"
            label="ABN*"
            type="text"
            name="abn"
            value={abn}
            onChange={setAbn}
            error={abnWarning}
            onBlur={() => Validate.validateAbn(abn, setAbnWarning, true) && setAbn(Validate.formatAbn(abn))}
          />

          <AuthField
            id="entityName"
            placeholder="Your organisation's registered legal name"
            label="Registered Name*"
            type="text"
            name="entityName"
            value={entityName}
            onChange={setEntityName}
            error={entityNameWarning}
            onBlur={() => Validate.validateEntityName(entityName, setEntityNameWarning, true)}
          />


        </AuthFormGroup>

        <FormControlLabel
          control={<Checkbox checked={emailListAgreed} onChange={emailListCheckboxHandler} />}
          label="Keep me posted about updates, features and offers!"
        />
        <FormControlLabel
          control={<Checkbox checked={termsAgreed} onChange={termsCheckboxHandler} />}
          label={
            (
              <Typography variant="body2">
                I agree to Clickability&apos;s&nbsp;
                <Link component={RouterLink} to="/terms">
                  <b>Terms and Conditions</b>
                </Link>
                &nbsp;&&nbsp;
                <Link component={RouterLink} to="/privacy">
                  <b>Privacy Policy.</b>
                </Link>*
              </Typography>
            )
          }
        />
        {showTermsWarning ? (<Alert severity="error">You must agree to the terms and privacy policy</Alert>) : null}
        <Markdown content={content.body2}/>
      </AuthForm>
      <Typography variant="body2">
        Already a member?&nbsp;
        <Link component={RouterLink} to={loginLink}>
          <b>Log in</b>
        </Link>.
      </Typography>
    </React.Fragment>
  );
};

ProviderSignupForm.propTypes = {
  title: PropTypes.string,
  onComplete: PropTypes.func.isRequired,
  loginLink: PropTypes.string.isRequired,
  busy: PropTypes.bool.isRequired,
  buttonLabel: PropTypes.string,
  signupError: PropTypes.string,
  provider: PropTypes.object,
};

export default ProviderSignupForm;
